import { apiFetch } from "../api.js";
import type { MerrillLot, StatementRow } from "../util/file-import.js";

export interface MerrillReplacePlan {
  existing_lot_count: number;
  new_lot_count: number;
  existing_cost_basis_cents: number;
  new_cost_basis_cents: number;
  tickers_added: string[];
  tickers_removed: string[];
  applied: boolean;
}

export function importMerrillLots(input: {
  account_id: string;
  external_id: string;
  as_of_date: string;
  dry_run: boolean;
  lots: readonly MerrillLot[];
}): Promise<MerrillReplacePlan> {
  return apiFetch("/v1/accounts/import-merrill", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function importMerrillBalances(input: {
  as_of_date: string;
  balances: readonly {
    account_id: string;
    cash_balance_cents: number;
    margin_balance_cents: number;
  }[];
}): Promise<{ updated: number }> {
  return apiFetch("/v1/accounts/import-merrill-balances", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

const IMPORT_BATCH_SIZE = 500;

/** Sends rows in batches and adds up the results. */
export async function importStatementRows(
  accountId: string,
  rows: readonly StatementRow[],
): Promise<{ added: number; skipped: number }> {
  let added = 0;
  let skipped = 0;
  for (let i = 0; i < rows.length; i += IMPORT_BATCH_SIZE) {
    const batch = rows.slice(i, i + IMPORT_BATCH_SIZE).map((row) => ({
      date: row.date,
      amount_cents: row.amount_cents,
      merchant: row.merchant.slice(0, 200),
      source_ref: row.source_ref.slice(0, 500),
      ...(row.category !== undefined && { category: row.category.slice(0, 100) }),
    }));
    const result = await apiFetch<{ added: number; skipped: number }>(
      "/v1/spending-transactions/import",
      {
        method: "POST",
        body: JSON.stringify({ account_id: accountId, rows: batch }),
      },
    );
    added += result.added;
    skipped += result.skipped;
  }
  return { added, skipped };
}
